import type { PlanEventDraft } from "./draft";
import type {
  MarketingCampaignSource,
  MarketingCampaignStatus,
} from "../schemas";

export type PlanEventStep = "details" | "broadcasts" | "review";

interface PlanDraftEntry {
  draft: PlanEventDraft;
  step: PlanEventStep;
  /** Set when the wizard is editing an existing campaign: saving PATCHes
   * this id instead of creating. Entering an edit seeds the registry and
   * marks a return, so the wizard resumes it like a round trip. */
  editingCampaignId?: string | null;
  /** The edited campaign's stored status, so the wizard knows whether the
   * review-step action needs a :schedule / :unschedule call after the PATCH
   * (status itself is ignored on PATCH). */
  editingCampaignStatus?: MarketingCampaignStatus | null;
  /** "agent" makes review's Delete a reject-with-reason (FOG-2271). */
  editingCampaignSource?: MarketingCampaignSource | null;
  /** Month ("YYYY-MM") the calendar was on when the wizard opened, so a
   * create-flow exit lands back there even after a composer round trip
   * drops the URL param (FOG-2538). */
  returnMonth?: string;
}

/**
 * Module-level, per-site persistence for the in-progress plan, so the wizard
 * survives navigating to the real broadcast edit pages and back (same idea as
 * BroadcastV2's compose draft registry). SPA-session only — a reload starts
 * planning over.
 */
const registry = new Map<string, PlanDraftEntry>();
// Sites with a BroadcastV2 round trip in flight. Only a marked return
// resumes the plan; any other visit to the wizard starts fresh.
const awaitingReturn = new Set<string>();

export const planDraftRegistry = {
  get(siteId: string): PlanDraftEntry | undefined {
    return registry.get(siteId);
  },
  set(siteId: string, entry: PlanDraftEntry): void {
    registry.set(siteId, entry);
  },
  clear(siteId: string): void {
    registry.delete(siteId);
    awaitingReturn.delete(siteId);
  },
  /** Call right before navigating out to the BroadcastV2 composer/editor. */
  markReturn(siteId: string): void {
    awaitingReturn.add(siteId);
  },
  /** True exactly once per marked round trip. */
  consumeReturn(siteId: string): boolean {
    const marked = awaitingReturn.has(siteId);
    awaitingReturn.delete(siteId);
    return marked;
  },
};
